/**
 * Dashboard — Admin Overview
 *
 * Rebuilt against the Aug 5 review. Reads two endpoints:
 *   /analytics/detailed   — the billing waterfall (Shopify orders, revenue, deductions)
 *   /dashboard/summary    — connector, catalogue, sync health, logistics SLA, access requests
 *
 * Two rules this screen holds to:
 *   1. Shopify orders and retailer orders are separate cards. Retailer orders BECOME
 *      Shopify orders, so the first contains the second — showing one number invited
 *      the reading that they were alternatives.
 *   2. A figure we cannot compute honestly renders as "—" with an explanation, never
 *      as 0. A zero here means "we checked and it is zero".
 */

import React, { useState, useCallback, useMemo } from 'react';
import {
  Page,
  Layout,
  Card,
  InlineGrid,
  Text,
  Box,
  BlockStack,
  InlineStack,
  Badge,
  Button,
  Spinner,
  Banner,
  Select,
  Popover,
  ChoiceList,
  Divider,
  Tooltip,
} from '@shopify/polaris';
import { useNavigate } from 'react-router-dom';

import MetricCard from '../components/Analytics/MetricCard';

/**
 * The Dashboard's tile: MetricCard at the compact padding.
 *
 * MetricCard's padding is real now — its three Boxes used to pass `padding` alongside a
 * `style` prop, and a style prop on Polaris Box replaces the generated style attribute
 * outright, so the declared padding never reached the page. With that fixed, every consumer
 * gets the 300 the component always intended, and this screen opts down to 200 because it is
 * the one being compacted. Analytics keeps 300.
 */
const Tile = (props) => <MetricCard padding="200" {...props} />;
import apiClient from '../utils/api';
import { useBrands, useDetailedAnalytics, useDashboardSummary } from '../utils/queries';
import { validateAnalyticsData, validateBrandsData, createSafeDataAccessor, validateApiParams } from '../utils/dataValidation';

const PERIOD_OPTIONS = [
  { label: 'Last 7 days', value: '7' },
  { label: 'Last 30 days', value: '30' },
  { label: 'Last 90 days', value: '90' },
  { label: 'Month to date', value: 'mtd' },
  { label: 'Year to date', value: 'ytd' },
];

/** mtd/ytd expressed as a day count, so one param serves both endpoints */
function daysFor(period) {
  const now = new Date();
  if (period === 'mtd') return Math.max(1, now.getDate());
  if (period === 'ytd') {
    return Math.max(1, Math.ceil((now - new Date(now.getFullYear(), 0, 1)) / 86400000));
  }
  return Number(period) || 30;
}

/**
 * Dashboard links must land somewhere real. Tracking and Inventory Sync do not have
 * dedicated pages yet, so both point at /connector, which is where that data lives
 * today. Repoint them here the moment those screens land — a link that silently
 * redirects to the dashboard is worse than no link.
 */
const ROUTES = {
  accessRequests: '/access-requests',
  orders: '/orders',
  // Tracking pushes come FROM Shopify fulfillments, so the orders themselves are where an
  // admin acts on a missing number — not the Tracking page, which reports the push.
  ordersMissingTracking: '/orders?tracking=missing',
  tracking: '/tracking',
  inventorySync: '/inventory-sync',
  connections: '/connector',
};

const fmtLag = (m) => (m == null ? '—' : m < 60 ? `${m} min` : `${(m / 60).toFixed(1)} hrs`);

export default function Dashboard() {
  const navigate = useNavigate();
  const [period, setPeriod] = useState('30');
  const [selectedBrands, setSelectedBrands] = useState([]);   // empty = all
  const [brandPickerOpen, setBrandPickerOpen] = useState(false);

  const days = daysFor(period);
  const brandParam = selectedBrands.length ? selectedBrands.join(',') : undefined;

  // `brand` is comma-separated, exactly as Analytics.jsx sends it — the server's
  // parseBrandIds splits on commas and treats 'all'/absent as no restriction.
  //
  // This used to read `selectedBrands.length === 1 ? selectedBrands[0] : 'all'`, which
  // collapsed EVERY multi-brand selection back to 'all'. Picking three brands out of
  // twenty silently returned the figures for all twenty, and the header above still
  // said "3 brands" — so the numbers looked filtered and were not. One brand worked,
  // which is why it survived: the only case anyone tested was the only case that was
  // right. 'all' is kept for the empty selection to preserve the existing wire format.
  const analyticsParams = useMemo(
    () => validateApiParams({ brand: brandParam || 'all', days }),
    [brandParam, days]
  );
  const summaryParams = useMemo(
    () => ({ days, ...(brandParam ? { brands: brandParam } : {}) }),
    [days, brandParam]
  );

  const { data: brandsResponse, isLoading: brandsLoading, error: brandsError } = useBrands();
  const { data: analyticsResponse, isLoading: analyticsLoading, error: analyticsError } =
    useDetailedAnalytics(analyticsParams);
  const { data: summary, isLoading: summaryLoading, error: summaryError } =
    useDashboardSummary(summaryParams);

  const brandsAccessor = useMemo(
    () => createSafeDataAccessor(brandsResponse, validateBrandsData), [brandsResponse]);
  const analyticsAccessor = useMemo(
    () => createSafeDataAccessor(analyticsResponse, validateAnalyticsData), [analyticsResponse]);

  const brands = brandsAccessor.data;
  const analytics = analyticsAccessor.data;

  const brandChoices = useMemo(
    () => (Array.isArray(brands) ? brands : [])
      .filter((b) => b && b.isActive !== false)
      .map((b) => ({ label: b.name, value: b.id })),
    [brands]
  );

  const activeBrandCount = useMemo(
    () => (Array.isArray(brands) ? brands.filter((b) => b && b.isActive !== false).length : 0),
    [brands]
  );
  const totalBrandCount = Array.isArray(brands) ? brands.length : 0;

  const brandLabel = selectedBrands.length === 0
    ? 'All brands'
    : selectedBrands.length === 1
      ? (brandChoices.find((c) => c.value === selectedBrands[0])?.label ?? '1 brand')
      : `${selectedBrands.length} brands`;

  const money = useCallback((v) => apiClient.formatCurrency(v ?? 0), []);
  const loading = analyticsLoading || summaryLoading;
  const cardErr = analyticsError || (!analyticsAccessor.isValid ? 'Invalid data format' : null);

  const syncErrors = summary?.syncHealth?.errorsByType ?? {};
  const totalSyncErrors =
    (syncErrors.orderSync ?? 0) + (syncErrors.inventorySync ?? 0) + (syncErrors.trackingSync ?? 0);
  const log = summary?.logistics ?? {};
  const cat = summary?.catalogue ?? {};

  // Needs-attention rows are derived, never hand-written — each one is a real count
  // with somewhere to go. If nothing is wrong, the card says so rather than vanishing.
  const attention = useMemo(() => {
    const a = [];
    if (!summary) return a;
    if (summary.accessRequests?.pending > 0)
      a.push({ tone: 'warning', text: `${summary.accessRequests.pending} access request${summary.accessRequests.pending > 1 ? 's' : ''} pending`, to: ROUTES.accessRequests, cta: 'Review' });
    if (log.unshipped48h > 0)
      a.push({ tone: 'critical', text: `${log.unshipped48h} order${log.unshipped48h > 1 ? 's' : ''} unshipped over 48 hours`, to: ROUTES.tracking, cta: 'View' });
    if (log.unshipped24h > 0)
      a.push({ tone: 'warning', text: `${log.unshipped24h} order${log.unshipped24h > 1 ? 's' : ''} unshipped over 24 hours`, to: ROUTES.tracking, cta: 'View' });
    if (cat.skusRejected > 0)
      a.push({ tone: 'critical', text: `${cat.skusRejected} inventory SKU${cat.skusRejected > 1 ? 's' : ''} rejected by a retailer`, to: ROUTES.inventorySync, cta: 'View' });
    if (log.missingTracking > 0)
      a.push({ tone: 'warning', text: `${log.missingTracking} fulfilled retailer order${log.missingTracking > 1 ? 's' : ''} with no tracking number`, to: ROUTES.ordersMissingTracking, cta: 'View' });
    if (summary.syncHealth?.connectionsAffected > 0)
      a.push({ tone: 'critical', text: `${summary.syncHealth.connectionsAffected} connection${summary.syncHealth.connectionsAffected > 1 ? 's' : ''} reporting sync errors`, to: ROUTES.connections, cta: 'View' });
    return a;
  }, [summary, log, cat]);

  if (brandsLoading && !brands) {
    return (
      <Page title="Dashboard" fullWidth>
        <Layout>
          <Layout.Section>
            <Card><Box padding="800"><InlineStack align="center"><Spinner /></InlineStack></Box></Card>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  if (brandsError || (analyticsError && summaryError)) {
    return (
      <Page title="Dashboard" fullWidth>
        <Layout>
          <Layout.Section>
            <Banner tone="critical">
              <BlockStack gap="200">
                <Text fontWeight="semibold">Unable to load dashboard data</Text>
                {brandsError && <Text variant="bodySm">Brands: {brandsError.message}</Text>}
                {analyticsError && <Text variant="bodySm">Analytics: {analyticsError.message}</Text>}
                {summaryError && <Text variant="bodySm">Summary: {summaryError.message}</Text>}
              </BlockStack>
            </Banner>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  // A heading NODE, not a Layout.Section of its own.
  //
  // Every band used to be two sections — one holding a single line of text, then one holding
  // its tiles — and Polaris puts its full inter-section gap between them. Five bands meant
  // five of those gaps spent separating a heading from the row it labels, which is the one
  // place it should not be. `band()` below now emits one section per band with the heading
  // tight above its own tiles.
  const section = (title, hint) => (
    <InlineStack gap="200" blockAlign="baseline">
      <Text variant="headingXs" as="h2" tone="subdued">{title}</Text>
      {hint && <Text variant="bodySm" tone="subdued">{hint}</Text>}
    </InlineStack>
  );
  /**
   * One band: its heading and its metric tiles, in a single Layout.Section.
   *
   * One column on mobile, stepping up to `n` across on desktop. gap 300 rather than 400
   * between tiles — at 400 a five-across row of small numbers read as five separate cards
   * rather than one band of related figures.
   */
  const band = (heading, n, children) => (
    <Layout.Section>
      <BlockStack gap="200">
        {heading}
        <InlineGrid gap="300" columns={{ xs: 1, sm: 2, md: 3, lg: Math.min(n, 4), xl: n }}>
          {children}
        </InlineGrid>
      </BlockStack>
    </Layout.Section>
  );

  return (
    <Page fullWidth>
      <Layout>
        {/* Title and controls share one row, as in the wireframe — Polaris Page cannot
            take arbitrary nodes in its header, so the header is rendered here instead. */}
        <Layout.Section>
          <InlineStack align="space-between" blockAlign="center" gap="400" wrap>
            <BlockStack gap="050">
              <Text variant="headingLg" as="h1">Dashboard</Text>
              <Text variant="bodySm" tone="subdued">
                {brandLabel} · {PERIOD_OPTIONS.find((p) => p.value === period)?.label.toLowerCase()}
              </Text>
            </BlockStack>
            <InlineStack gap="200" blockAlign="center" wrap>
              {summaryLoading && <Spinner size="small" />}
              <Select label="Period" labelHidden options={PERIOD_OPTIONS} value={period} onChange={setPeriod} />
              <Popover
                active={brandPickerOpen}
                activator={<Button disclosure onClick={() => setBrandPickerOpen((o) => !o)}>{brandLabel}</Button>}
                onClose={() => setBrandPickerOpen(false)}
              >
                <Box padding="300" minWidth="240px">
                  <BlockStack gap="200">
                    <ChoiceList
                      allowMultiple title="Brands" titleHidden
                      choices={brandChoices} selected={selectedBrands} onChange={setSelectedBrands}
                    />
                    {selectedBrands.length > 0 && (
                      <Button variant="plain" onClick={() => setSelectedBrands([])}>
                        Clear — show all brands
                      </Button>
                    )}
                  </BlockStack>
                </Box>
              </Popover>
            </InlineStack>
          </InlineStack>
        </Layout.Section>

        {/* ---------------- Sales ---------------- */}
        {band(section('Sales'), 5, <>
                          <Tile
                title="Shopify Orders" value={analytics.totalOrders} loading={loading} error={cardErr}
                tooltip="Every billable Shopify order across the selected brands — whatever falls inside each brand's billing scope. Retailer orders are included here, because they become Shopify orders."
                subtitle="includes retailer orders"
              />
                          <Tile
                title="Retailer Orders" value={summary?.connector?.retailerOrders} loading={summaryLoading} error={summaryError}
                tooltip="Orders imported from retailers by the connector."
                subtitle={`from ${summary?.connector?.connectedRetailers ?? 0} retailers`}
              />
            <Tile title="Gross Revenue" value={analytics.totalGrossRevenue} loading={loading} error={cardErr} formatter={money}
              tooltip="Revenue across the selected brands and period, from fulfilled orders." />
            <Tile title="Refunds" value={analytics.totalRefunds} loading={loading} error={cardErr} formatter={money}
              tooltip="Total refunds in the period." />
            <Tile title="Net Sales" value={analytics.totalNetSales} loading={loading} error={cardErr} formatter={money}
              tooltip="Line-item sales after discounts and refunds. Discounts are imported and deducted here." />
        </>)}

        {/* ---------------- Deductions ---------------- */}
        {band(section('Deductions'), 5, <>
            <Tile title="Marketplace Commission" value={analytics.commissionBreakdown?.baseMarketplaceCommission || 0}
              loading={loading} error={cardErr} formatter={money}
              tooltip="Commission charged by the retailers on fulfilled orders, before adjustments. The rate differs per retailer." />
            <Tile title="2W Commission" value={analytics.commissionBreakdown?.base2wCommission || 0}
              loading={loading} error={cardErr} formatter={money}
              tooltip="2W service commission on fulfilled orders, before adjustments." />
            <Tile title="Commission Adjustments" color={(analytics.commissionBreakdown?.totalAdjustmentAmount || 0) >= 0 ? 'success' : 'critical'} value={analytics.commissionBreakdown?.totalAdjustmentAmount || 0}
              loading={loading} error={cardErr} formatter={money}
              tooltip="Manual additions and deductions applied in this period." />
            <Tile title="Shipping Fees" value={analytics.totalShippingFees} loading={loading} error={cardErr} formatter={money}
              tooltip="Shipping fees from the configured BoxC rate table, by service and weight." />
            <Tile title="Return Fees" value={analytics.totalReturnFees} loading={loading} error={cardErr} formatter={money}
              tooltip="Return fees from the configured default rate and per-retailer overrides." />
        </>)}

        {/* ---------------- Status ---------------- */}
        {band(section('Status'), 3, <>
            <Tile title="Active Brands" value={activeBrandCount} loading={brandsLoading} error={brandsError}
              subtitle={totalBrandCount ? `of ${totalBrandCount} total` : null}
              tooltip="Brands currently active and processing orders." />
            <Tile title="Connected Retailers" value={summary?.connector?.connectedRetailers} loading={summaryLoading} error={summaryError}
              tooltip="Distinct retailer accounts configured, counted once each." />
            <Tile title="Brand–Retailer Connections" value={summary?.connector?.brandRetailerConnections} loading={summaryLoading} error={summaryError}
              tooltip="Every brand-to-retailer link. A brand connected to three retailers counts three times — this is the number that grows as brands are onboarded, unlike the retailer count." />
        </>)}

        {/* ---------------- Access requests ---------------- */}
        {band(section('Access requests', 'brands asking for the Shopify app'), 3, <>
            <Tile title="Pending" value={summary?.accessRequests?.pending} loading={summaryLoading} error={summaryError}
              color={summary?.accessRequests?.pending > 0 ? 'warning' : 'default'}
              tooltip="Brands that have submitted a request and are waiting on a decision." />
            <Tile title="Approved" value={summary?.accessRequests?.approved} loading={summaryLoading} error={summaryError}
              tooltip="Brands approved and using the app." />
            <Tile title="Rejected" value={summary?.accessRequests?.rejected} loading={summaryLoading} error={summaryError} tooltip="Requests declined. The brand can resubmit." />
        </>)}

        {/* The "Catalogue" band was REMOVED. Do not restore it — all four of its tiles were
            either ambiguous or duplicated, and the questions they answered are answered
            properly elsewhere.
              - "SKUs Listed on Mirakl" counted once PER RETAILER, as its own tooltip admitted:
                one SKU listed with three retailers read as 3. So the headline was a multiple of
                the real catalogue with nothing on the tile to say by how much.
              - "Products Listed on Mirakl" was distinct products behind those SKUs — a
                different denominator sitting beside the first, so two numbers that looked
                comparable were not. It also carried a "Backfill pending" badge conceding it
                could undercount.
              - "SKUs Rejected by Retailer" is a real fault, but it is already a needs-attention
                row with a count and a View link (see `attention` above, which still reads
                cat.skusRejected). The tile was a worse copy of that row.
              - "Unmapped SKUs" coloured itself `critical` above zero, asserting a fault where
                there usually is none — a brand not listing its whole Shopify catalogue at every
                retailer is the normal case, so it sat red permanently. Its count also
                over-reported as listed: countUnmappedSkus (shopify-catalogue-service.js:345)
                joins on retailer account + SKU code with NO brand match, while the SKU Map table
                also requires the retailer's productBrand to match one of the brand's three
                candidate names (sku-map-service.js:430 has the whole account).
            Where these questions now live: the SKU Map table on Retailer Accounts, which is per
            brand and per SKU, carries a Retailer column and a brand filter, and has a
            "Not listed" filter that names the SKU instead of totalling it.
            The API still returns summary.catalogue; `cat` is kept for the attention row. */}

        {/* ---------------- Sync health | Logistics ---------------- */}
        <Layout.Section>
          {/* InlineGrid, not Grid/Grid.Cell — a grid item only stretches to the row height
              when it IS the direct child of the grid container. Grid.Cell was that child, so
              the Cell matched its sibling but the Card inside it stayed at content height and
              left a gap below. Card as the direct item fills the row, so both cards end level.
              Every other card row on this page already uses InlineGrid for the same reason. */}
          <InlineGrid columns={{ xs: 1, md: 2 }} gap="300">
            <Card>
                <Box padding="200">
                  <BlockStack gap="200">
                    <InlineStack align="space-between" blockAlign="center">
                      <Text variant="headingMd" as="h3">Sync health</Text>
                      <Badge tone={totalSyncErrors > 0 ? 'critical' : 'success'}>
                        {totalSyncErrors > 0
                          ? `${summary?.syncHealth?.connectionsAffected ?? 0} connection${(summary?.syncHealth?.connectionsAffected ?? 0) === 1 ? '' : 's'} failing`
                          : 'All healthy'}
                      </Badge>
                    </InlineStack>
                    <Text variant="bodySm" tone="subdued">Is data moving between Shopify and the retailers.</Text>

                    <Text variant="headingXs" tone="subdued">Errors by type</Text>
                    {[
                      ['Order sync errors', syncErrors.orderSync, ROUTES.orders],
                      ['Inventory sync errors', syncErrors.inventorySync, ROUTES.inventorySync],
                      ['Tracking sync errors', syncErrors.trackingSync, ROUTES.tracking],
                    ].map(([label, count, to]) => (
                      <InlineStack key={label} align="space-between" blockAlign="center">
                        <Text variant="bodySm">{label}</Text>
                        <Button variant="plain" tone={count > 0 ? 'critical' : undefined} onClick={() => navigate(to)}>
                          {count ?? 0}
                        </Button>
                      </InlineStack>
                    ))}
                    <Divider />
                    <InlineStack align="space-between">
                      <Text variant="bodySm" tone="subdued">Last inventory sync</Text>
                      <Text variant="bodySm">{summary?.syncHealth?.lastInventorySync ? new Date(summary.syncHealth.lastInventorySync).toLocaleString() : '—'}</Text>
                    </InlineStack>
                    <InlineStack align="space-between">
                      <Text variant="bodySm" tone="subdued">Last tracking push</Text>
                      <Text variant="bodySm">{summary?.syncHealth?.lastTrackingPush ? new Date(summary.syncHealth.lastTrackingPush).toLocaleString() : '—'}</Text>
                    </InlineStack>
                  </BlockStack>
                </Box>
              </Card>

            <Card>
                <Box padding="200">
                  <BlockStack gap="200">
                    <InlineStack align="space-between" blockAlign="center">
                      <Text variant="headingMd" as="h3">Logistics &amp; shipping</Text>
                      {log.unshipped48h > 0 && <Badge tone="critical">{`${log.unshipped48h} over 48h`}</Badge>}
                    </InlineStack>
                    {/* "Retailer orders only" is the load-bearing half. Every row here counts
                        connector orders — on-time rate and tracking lag can come from nowhere
                        else — so a brand selling direct reads zero throughout, which is
                        correct and looks broken unless the card says so. The brand Analytics
                        tab counts every order and will show more. */}
                    <Text variant="bodySm" tone="subdued">
                      How fast retailer orders leave, and which ones have not. Retailer orders only.
                    </Text>

                    <InlineStack align="space-between" blockAlign="center">
                      <Text variant="bodySm">Unshipped over 24 hours</Text>
                      <Button variant="plain" tone={log.unshipped24h > 0 ? 'critical' : undefined} onClick={() => navigate(ROUTES.tracking)}>{log.unshipped24h ?? 0}</Button>
                    </InlineStack>
                    <InlineStack align="space-between" blockAlign="center">
                      <Text variant="bodySm">Unshipped over 48 hours</Text>
                      <Button variant="plain" tone={log.unshipped48h > 0 ? 'critical' : undefined} onClick={() => navigate(ROUTES.tracking)}>{log.unshipped48h ?? 0}</Button>
                    </InlineStack>
                    <InlineStack align="space-between" blockAlign="center">
                      <Text variant="bodySm">Missing tracking</Text>
                      <Button variant="plain" tone={log.missingTracking > 0 ? 'critical' : undefined} onClick={() => navigate(ROUTES.ordersMissingTracking)}>{log.missingTracking ?? 0}</Button>
                    </InlineStack>
                    <Divider />
                    <InlineStack align="space-between">
                      <Text variant="bodySm" tone="subdued">On-time shipping rate</Text>
                      {log.onTimeRate == null ? (
                        <Text variant="bodySm">—</Text>
                      ) : (
                        <Tooltip content={
                          log.slaReliable
                            ? `Based on ${log.onTimeSample} retailer orders whose 24-hour window has closed.`
                            : `Only ${log.slaCoverage}% of retailer orders carry the retailer's placed-at time, so this is not yet reliable.`
                        }>
                          <Text variant="bodySm" tone={log.slaReliable ? undefined : 'subdued'}>
                            {log.onTimeRate}% {!log.slaReliable && '(low coverage)'}
                          </Text>
                        </Tooltip>
                      )}
                    </InlineStack>
                    <InlineStack align="space-between">
                      <Text variant="bodySm" tone="subdued">Avg tracking lag</Text>
                      <Text variant="bodySm">{fmtLag(log.avgTrackingLagMinutes)}</Text>
                    </InlineStack>
                    <InlineStack align="space-between">
                      <Text variant="bodySm" tone="subdued">Open orders</Text>
                      <Text variant="bodySm">{log.openOrders ?? 0}</Text>
                    </InlineStack>
                    <InlineStack align="space-between">
                      {/* Named for what it counts, matching the Tracking page's first funnel
                          step. "Shipped in period" read as every order and counted only the
                          ones that came through a retailer. */}
                      <Text variant="bodySm" tone="subdued">Retailer orders fulfilled</Text>
                      <Text variant="bodySm">{log.shipped ?? 0}</Text>
                    </InlineStack>
                  </BlockStack>
                </Box>
              </Card>
          </InlineGrid>
        </Layout.Section>

        {/* ---------------- Needs attention ---------------- */}
        <Layout.Section>
          <Card>
            <Box padding="200">
              <BlockStack gap="200">
                <Text variant="headingMd" as="h3">Needs attention</Text>
                {attention.length === 0 ? (
                  <InlineStack gap="200" blockAlign="center">
                    <Badge tone="success">Clear</Badge>
                    <Text variant="bodySm" tone="subdued">
                      Nothing outstanding — no pending requests, no late shipments, no sync errors.
                    </Text>
                  </InlineStack>
                ) : (
                  /* A tinted Box rather than a Banner. Inside a Card, Banner renders the
                     withinContentContainer variant, which nests our children in a flex item
                     that shrink-to-fits its content — so `align="space-between"` had no free
                     width to distribute and each button landed just after its own text, at a
                     different x per row. A Box is a plain block that fills the card, so the
                     buttons all line up on one right edge regardless of text length. */
                  attention.map((a, i) => (
                    <Box
                      key={i}
                      background={a.tone === 'critical' ? 'bg-surface-critical' : 'bg-surface-warning'}
                      padding="300"
                      borderRadius="200"
                    >
                      <InlineStack align="space-between" blockAlign="center" gap="400" wrap={false}>
                        <Text variant="bodySm">{a.text}</Text>
                        <Button variant="plain" onClick={() => navigate(a.to)}>{a.cta}</Button>
                      </InlineStack>
                    </Box>
                  ))
                )}
              </BlockStack>
            </Box>
          </Card>
        </Layout.Section>
      </Layout>
    </Page>
  );
}
